<script lang="ts">
  import type { HTMLAttributes } from 'vue'
  export interface DataContainerAddressProps {
    class?: HTMLAttributes['class']
    data?: {
      address?: string
      countryName?: string
      countryCode?: string
    }
    addressTestId?: string
    countryNameTestId?: string
    showIcons?: boolean
    /** The text to display when there is no address data. @default 'No address.' */
    noDataText?: string
  }
</script>

<script setup lang="ts">
  import { computed, watch } from 'vue'
  import { useSelectCountryDataSource } from '../../composables/useSelectCountryDataSource'
  import { useDebounceFn } from '@vueuse/core'
  import { cn, FlagIcon } from '@gat-ship/vue-components'
  import { IconMapPin } from '@gat-ship/icons'
  const props = withDefaults(defineProps<DataContainerAddressProps>(), {
    noDataText: 'No address.',
    showIcons: true
  })

  const countryDataSource = useSelectCountryDataSource()

  function hasData() {
    return (
      props.data?.address || props.data?.countryName || props.data?.countryCode
    )
  }

  const fetchCountries = useDebounceFn(async () => {
    if (props.data?.countryCode) {
      await countryDataSource.fetchCountries()
    }
  }, 200)

  watch(
    () => props.data?.countryCode,
    async (val: string | undefined) => {
      if (val) {
        fetchCountries()
      }
    },
    {
      immediate: true
    }
  )

  const countryName = computed(() => {
    if (props.data?.countryCode) {
      return countryDataSource.countries.value?.find(
        (i) => i.code === props.data?.countryCode
      )?.name
    }
    return props.data?.countryName || props.data?.countryCode
  })
</script>

<template>
  <div v-if="hasData()" :class="cn('flex flex-col gap-0.5', props.class)">
    <div class="flex items-start gap-2">
      <div v-if="props.showIcons" class="mt-0.5">
        <icon-map-pin class="size-4" />
      </div>
      <div class="whitespace-pre-line">
        <span v-if="props.data?.address" :data-testid="props.addressTestId">{{
          props.data?.address
        }}</span>
      </div>
    </div>
    <div v-if="props.data?.countryCode" class="flex items-center gap-2">
      <FlagIcon
        v-if="props.showIcons"
        :flag="props.data?.countryCode"
        variant="square"
        class="size-4 rounded-full"
      />
      <span :data-testid="props.countryNameTestId">{{ countryName }}</span>
    </div>
  </div>
  <span v-else class="text-muted-foreground">{{ props.noDataText }}</span>
</template>
